import { useMemo, useState } from 'react';
import { FileText, Pencil, Search, Tags } from 'lucide-react';
import { useTags } from '../../queries/tags';
import { Track, useTracks } from '../../queries/tracks';
import { EditTrackTagsDialog } from './EditTrackTagsDialog';
import { EditTrackDetailsDialog } from './EditTrackDetailsDialog';
import { ManageTagsDialog } from './ManageTagsDialog';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const INPUT = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function TracksPage() {
  const { data: tracks = [], isLoading } = useTracks();
  const { data: tags = [] } = useTags();

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editingTrack, setEditingTrack] = useState<Track | null>(null);
  const [editingDetails, setEditingDetails] = useState<Track | null>(null);
  const [managingTags, setManagingTags] = useState(false);

  const states = useMemo(() => Array.from(new Set(tracks.map((t) => t.state))).sort(), [tracks]);
  const types = useMemo(() => Array.from(new Set(tracks.map((t) => t.type))).sort(), [tracks]);

  const filtered = tracks.filter((t) => {
    const matchesSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.code.toLowerCase().includes(search.toLowerCase());
    const matchesState = stateFilter === 'all' || t.state === stateFilter;
    const matchesType = typeFilter === 'all' || t.type === typeFilter;
    return matchesSearch && matchesState && matchesType;
  });

  const columns: DataTableColumn<Track>[] = [
    {
      key: 'track',
      header: 'Track',
      priority: true,
      render: (track) => (
        <span className="font-medium">
          {track.name}
          {track.display_name && track.display_name !== track.name && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">({track.display_name})</span>
          )}
        </span>
      ),
    },
    { key: 'state', header: 'State', priority: false, render: (t) => <span className="text-muted-foreground @2xl:text-foreground">{t.state}</span> },
    { key: 'type', header: 'Type', priority: false, render: (t) => <span className="text-muted-foreground @2xl:text-foreground">{t.type}</span> },
    { key: 'summary', header: 'Summary', priority: false, render: (t) => (t.track_summary ? <Badge variant="success">Yes</Badge> : <span className="text-muted-foreground">—</span>) },
    {
      key: 'tags',
      header: 'Tags',
      priority: false,
      render: (track) => (
        <div className="flex flex-wrap justify-end gap-1 @2xl:justify-start">
          {track.tags.map((tag) => (
            <Badge key={tag.id} variant="default">{tag.name}</Badge>
          ))}
          {track.tags.length === 0 && <span className="text-muted-foreground">—</span>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (track) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => setEditingDetails(track)} title="Edit details & summaries">
            <FileText className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setEditingTrack(track)} title="Edit tags">
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Tracks"
        description="Tag tracks to drive which packages include or exclude them."
        actions={
          <Button variant="outline" onClick={() => setManagingTags(true)}>
            <Tags className="h-4 w-4" />
            Manage tags
          </Button>
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[14rem] sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tracks…" className={`${INPUT} w-full pl-9`} />
        </div>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={INPUT}>
          <option value="all">All states</option>
          {states.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={INPUT}>
          <option value="all">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={filtered} getRowKey={(t) => t.track_id} emptyMessage="No tracks match the current filters." />
      )}

      {editingTrack && <EditTrackTagsDialog track={editingTrack} allTags={tags} onClose={() => setEditingTrack(null)} />}
      {editingDetails && <EditTrackDetailsDialog track={editingDetails} onClose={() => setEditingDetails(null)} />}
      {managingTags && <ManageTagsDialog onClose={() => setManagingTags(false)} />}
    </div>
  );
}
